import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, ShoppingBag, Home, Briefcase, Building2, FileCheck,
  Plane, GraduationCap, Grid3x3, ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const ICONS = { Wallet, ShoppingBag, Home, Briefcase, Building2, FileCheck, Plane, GraduationCap, Grid3x3 };
const TONE = {
  emerald: "text-emerald-600 bg-emerald-50",
  sky: "text-sky-600 bg-sky-50",
  amber: "text-amber-600 bg-amber-50",
  indigo: "text-indigo-600 bg-indigo-50",
  rose: "text-rose-600 bg-rose-50",
};

// Static category list (names/icons/colors/order) — rendered instantly so the
// section never shows blank skeletons. Kept in sync with the /categories seed.
const LOCAL_CATEGORIES = [
  { slug: "money", icon: "Wallet", color: "emerald", name_en: "Money & Personal Finance", name_hi: "पैसा और वित्त" },
  { slug: "shopping", icon: "ShoppingBag", color: "sky", name_en: "Shopping & Product Buying", name_hi: "खरीदारी" },
  { slug: "home", icon: "Home", color: "amber", name_en: "Home & Daily Life", name_hi: "घर और दैनिक जीवन" },
  { slug: "jobs", icon: "Briefcase", color: "indigo", name_en: "Jobs & Career", name_hi: "नौकरी और करियर" },
  { slug: "business", icon: "Building2", color: "rose", name_en: "Business & Freelancing", name_hi: "व्यापार और फ्रीलांसिंग" },
  { slug: "documents", icon: "FileCheck", color: "sky", name_en: "Documents & Government", name_hi: "दस्तावेज़ और सरकारी" },
  { slug: "travel", icon: "Plane", color: "emerald", name_en: "Travel & Commute", name_hi: "यात्रा और आवागमन" },
  { slug: "education", icon: "GraduationCap", color: "indigo", name_en: "Education & Learning", name_hi: "शिक्षा और सीखना" },
  { slug: "more", icon: "Grid3x3", color: "amber", name_en: "More Everyday Tools", name_hi: "अधिक टूल्स" },
];

export function CategorySection() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(LOCAL_CATEGORIES);

  // Render immediately from local data; silently reconcile with the backend
  // in the background (never blocks or shows a skeleton).
  useEffect(() => {
    let mounted = true;
    api.get("/categories")
      .then(({ data }) => { if (mounted && Array.isArray(data) && data.length) setCategories(data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleClick = (cat) => {
    navigate(`/category/${cat.slug}`);
  };

  return (
    <section id="categories" className="border-y border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("categories_title")}</h2>
          <p className="mt-2 text-slate-500">{t("categories_subtitle")}</p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          {categories.map((cat) => {
            const Icon = ICONS[cat.icon] || Grid3x3;
            const name = language === "hi" ? cat.name_hi : cat.name_en;
            return (
              <button
                key={cat.slug}
                data-testid={`category-${cat.slug}`}
                onClick={() => handleClick(cat)}
                className="group card-premium flex items-center gap-3 p-3.5 text-left sm:gap-3.5 sm:p-5"
              >
                <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-slate-900/5 sm:h-11 sm:w-11 ${TONE[cat.color] || TONE.sky}`}>
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold leading-tight text-slate-900 [overflow-wrap:anywhere]">{name}</span>
                </span>
                <ArrowUpRight className="hidden h-4 w-4 flex-shrink-0 text-slate-300 transition-colors group-hover:text-primary sm:block" />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
